import { useEffect } from 'react';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useQuery } from "@tanstack/react-query";
import FormSkeleton from '../components/FormSkeleton';
import ResultsSkeleton from '../components/ResultsSkeleton';
import Pagination from '../components/Pagination';
import PaginationSkeleton from '../components/PaginationSkeleton';
import PolicyHero from '../components/PolicyHero';
import Results from '../components/Results';

//Types

//utils
import { useListingFilters, buildFilterQueryString, CursorExpiredError } from "@/utils/listing-utils.ts";

/**
 * Route for the Policy Library Homepage, or Policy Library Listing
 */

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): ListingSearch => {
    const cat = Array.isArray(search.cat) ? search.cat.filter((c): c is string => typeof c === 'string') : typeof search.cat === 'string' ? [search.cat] : undefined;
    return {
      search: typeof search.search === 'string' ? search.search : undefined,
      // Drop an empty array so it doesn't linger in the URL as `cat=[]`.
      cat: cat && cat.length ? cat : undefined,
      letter: typeof search.letter === 'string' ? search.letter : undefined,
      pg: Number(search.pg) || undefined
    };
  },
  component: Home
});
function Home() {
  const hostname = window.location.hostname;
  const {
    search,
    resetPagination
  } = useListingFilters();

  /* Pagination position lives in history state (not the URL) so back/forward
     restores it within a session. `cursor` + `direction` drive the data fetch
     and `page` drives the display; reading them from the same history entry
     keeps them in lockstep. A cold/shared load has no state, so we fall back to
     page 1. */
  const {
    cursor,
    page,
    direction
  } = useRouterState({
    select: state => ({
      cursor: state.location.state.cursor,
      page: state.location.state.page,
      direction: state.location.state.direction
    })
  });
  const currentPage = page ?? 1;

  /* Query to get "static" content on the listing page */
  const staticContent = `https://${hostname}/wp-json/policy-library/v1/listing-static-content`;
  const {
    isPending: contentIsPending,
    error: contentError,
    data: contentData
  } = useQuery<StaticContent>({
    queryKey: ['static_content'],
    queryFn: async () => {
      const res = await fetch(staticContent);
      if (!res.ok) {
        throw new Error('Failed to fetch static content');
      }
      return res.json() as Promise<StaticContent>;
    },
    refetchOnWindowFocus: false
  });
  useEffect(() => {
    if (contentData?.page_title) {
      document.title = contentData.page_title;
    }
  }, [contentData?.page_title]);

  /* Query to get all labels for the category filter */
  const labels = `https://${hostname}/wp-json/policy-library/v1/labels`;
  const {
    isPending: labelsIsPending,
    error: labelsError,
    data: labelsData
  } = useQuery<Labels>({
    queryKey: ['labels'],
    queryFn: async () => {
      const res = await fetch(labels);
      if (!res.ok) {
        throw new Error('Failed to fetch labels');
      }
      return res.json() as Promise<Labels>;
    },
    select: (data): Labels => ({
      //remove the "startswith" labels since those are for the alpha filter
      ...data,
      results: data.results.filter(label => !label.name.startsWith('startswith'))
    }),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchInterval: false
  });

  /* Query to get results.
     `pg` is intentionally left out of the fetch/key — the backend paginates on
     cursor + direction, not offset. `pg` stays in the URL only to give each
     page a distinct history entry in the browser history stack. */
  const results = `https://${hostname}/wp-json/policy-library/v1/filter`;
  const filterQuery = buildFilterQueryString(search);
  const {
    isPending: resultsIsPending,
    error: resultsError,
    data: resultsData
  } = useQuery<SearchResults, Error, GroupedSearchResults>({
    queryKey: ['results', filterQuery, cursor ?? 'first', direction ?? 'next'],
    queryFn: async () => {
      const params = new URLSearchParams(filterQuery);
      if (cursor) {
        params.set('cursor', cursor);
        params.set('direction', direction ?? 'next');
      }
      const query = params.toString();
      const url = `${results}${query ? `?${query}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) {
        /* A 409 here means the backend handed our cursor to Confluence
           and it was rejected (expired/invalid). Surface it as a typed
           error so we can quietly reset to page 1 instead of erroring. */
        if (cursor && res.status === 409) {
          throw new CursorExpiredError();
        }
        throw new Error('Failed to fetch results');
      }
      return res.json() as Promise<SearchResults>;
    },
    /* Don't burn the default retries on a dead cursor — we recover by
       resetting to the first page below. */
    retry: (failureCount, error) => !(error instanceof CursorExpiredError) && failureCount < 3,
    select: (data): GroupedSearchResults => {
      const grouped = data.results.reduce<Record<string, SearchResult[]>>((groups, result) => {
        //transform data so that data is grouped in an array with objects containing
        //the first letter of the title, and the results an array within that object.
        //For instance: [{A: [{..}, {..}, ..]}, {B: [{..}, {..}, ...]}]
        const letter = result.content.title.trim().charAt(0).toUpperCase();
        if (!groups[letter]) {
          groups[letter] = [];
        }
        groups[letter].push(result);
        return groups;
      }, {});
      return {
        ...data,
        results: grouped
      };
    },
    refetchOnWindowFocus: false
  });

  /* If the cursor was rejected at the source, drop back to page 1. Clearing
     the cursor changes the query key, so a fresh first-page request runs
     automatically. The fallback only fires when a cursor was in play, so it
     can't loop on a genuine first-page error. */
  useEffect(() => {
    if (resultsError instanceof CursorExpiredError) {
      resetPagination();
    }
  }, [resultsError, resetPagination]);

  //Conditionals regarding when to show results and pagination components
  /* A cursor failure isn't a hard error — we're mid-recovery (resetting to
     page 1), so treat it like a loading state and suppress the error UI. */
  const isCursorRecovery = resultsError instanceof CursorExpiredError;
  const isLoadingResults = resultsIsPending && !resultsError || isCursorRecovery;
  const hasResults = !resultsIsPending && !resultsError && !!resultsData;
  const isHardError = !resultsIsPending && !!resultsError && !isCursorRecovery;
  const hasPagination = hasResults && Boolean(resultsData.nextCursor || resultsData.prevCursor);
  /* Show while paginating (not on initial page-1 load or filter resets). */
  const showPaginationSkeleton = isLoadingResults && (currentPage > 1 || Boolean(cursor));
  return <>
      <section className="policy-library-listing-hero">
          <div aria-live="polite" aria-atomic="true" className="invis">
              {contentIsPending ? 'Content loading.' : contentError ? 'Content unavailable.' : 'Content loaded.'}
          </div>
          {(contentIsPending || labelsIsPending) && <FormSkeleton />}
          {!contentIsPending && !labelsIsPending && (contentError || labelsError) && <p className="error">This feature is currently experiencing a technical error. Please contact us if this issue persists.</p>}
          {!contentIsPending && !contentError && contentData && !labelsIsPending && !labelsError && labelsData && <PolicyHero contentData={contentData} categories={labelsData?.results} />}

      </section>
      <section className="policy-library-listing-results">
          {isLoadingResults && <ResultsSkeleton />}
          {isHardError && <p className="error">This feature is currently experiencing a technical error. Please contact us if this issue persists.</p>}
          {hasResults && <Results data={resultsData} currentPage={currentPage} />}
      </section>
      {showPaginationSkeleton && <PaginationSkeleton currentPage={currentPage} />}
      {hasPagination && <Pagination currentPage={currentPage} nextCursor={resultsData.nextCursor} prevCursor={resultsData.prevCursor} />}
      </>;
}
const hot = import.meta.webpackHot;
if (hot && typeof window !== 'undefined') {
  ;
  hot.data ??= {};
  const tsrReactRefresh = window.__TSR_REACT_REFRESH__ ??= (() => {
    const ignoredExportsById = new Map();
    const previousGetIgnoredExports = window.__getReactRefreshIgnoredExports;
    window.__getReactRefreshIgnoredExports = ctx => {
      const ignoredExports = previousGetIgnoredExports?.(ctx) ?? [];
      const moduleIgnored = ignoredExportsById.get(ctx.id) ?? [];
      return [...ignoredExports, ...moduleIgnored];
    };
    return {
      ignoredExportsById
    };
  })();
  tsrReactRefresh.ignoredExportsById.set("/var/www/html/wp-content/mu-plugins/princeton-policy-library/policy-library-app/src/routes/index.tsx", ['Route']);
}
export function TSRFastRefreshAnchor() {
  return null;
}
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  hot.accept(newModule => {
    if (Route && newModule && newModule.Route) {
      const routeId = hotData['tsr-route-id'] ?? Route.id;
      if (routeId) {
        hotData['tsr-route-id'] = routeId;
      }
      (function handleRouteUpdate(routeId, newRoute) {
        const router = window.__TSR_ROUTER__;
        const oldRoute = router.routesById[routeId];
        if (!oldRoute) return;
        const removedKeys = new Set();
        Object.keys(oldRoute.options).forEach(key => {
          if (!(key in newRoute.options)) {
            removedKeys.add(key);
            delete oldRoute.options[key];
          }
        });
        ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"].forEach(key => {
          if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
        });
        oldRoute.options = newRoute.options;
        oldRoute.update(newRoute.options);
        oldRoute._componentsPromise = void 0;
        oldRoute._lazyPromise = void 0;
        router.routesById[oldRoute.id] = oldRoute;
        router.routesByPath[oldRoute.fullPath] = oldRoute;
        router.processedTree.matchCache.clear();
        router.processedTree.flatCache?.clear();
        router.processedTree.singleCache.clear();
        router.resolvePathCache.clear();
        walkReplaceSegmentTree(oldRoute, router.processedTree.segmentTree);
        const filter = m => m.routeId === oldRoute.id;
        const activeMatch = router.stores.matches.get().find(filter);
        const pendingMatch = router.stores.pendingMatches.get().find(filter);
        const cachedMatches = router.stores.cachedMatches.get().filter(filter);
        if (activeMatch || pendingMatch || cachedMatches.length > 0) {
          if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
            const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
            router.batch(() => {
              for (const matchId of matchIds) {
                const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
                if (store) store.set(prev => {
                  const next = {
                    ...prev
                  };
                  if (removedKeys.has("loader")) next.loaderData = void 0;
                  if (removedKeys.has("beforeLoad")) next.__beforeLoadContext = void 0;
                  return next;
                });
              }
            });
          }
          router.invalidate({
            filter,
            sync: true
          });
        }
        function walkReplaceSegmentTree(route, node) {
          if (node.route?.id === route.id) node.route = route;
          if (node.index) walkReplaceSegmentTree(route, node.index);
          node.static?.forEach(child => walkReplaceSegmentTree(route, child));
          node.staticInsensitive?.forEach(child => walkReplaceSegmentTree(route, child));
          node.dynamic?.forEach(child => walkReplaceSegmentTree(route, child));
          node.optional?.forEach(child => walkReplaceSegmentTree(route, child));
          node.wildcard?.forEach(child => walkReplaceSegmentTree(route, child));
        }
      })(routeId, newModule.Route);
    }
  });
}